import React from 'react';
import NCEditor from '../NCEditor';
import OrigemBadge from './OrigemBadge';
import { normalizarAcaoCorretiva } from '../../utils/acoesAdapter';
import { formatHipoteseAlternativaPorque } from '../../utils/porqueFormat';
import { cssSlug } from '../../utils/cssSlug';
import { getSeveridadeLabel, getSeveridadeCor, corTextoContraste } from '../../utils/severidade';
import { diagnosticarPilarPrincipal } from '../../utils/pilarCoerencia';
import { ramo6MTemConteudo } from '../../utils/ishikawa';

// TD #48 (Sessão 24): botão "Recalcular com IA" + modal CascataIAModal
// foram movidos pro NCResult.jsx (banner contextual smart). Helper
// isCascataIAHabilitada() também migrou — agora vive em NCResult onde o
// banner consome.

/**
 * Seção completa de análise da NC
 * @param {object} analise - Dados da análise
 * @param {object} analiseV4 - Dados da análise v4.0
 * @param {string} currentStatus - Status atual (para mostrar editor)
 * @param {number} ncId - ID da NC
 * @param {object} nc - Sessão 19: objeto NC base (contexto_processo,
 *                      contexto_setorial_custom, industria). Passado pro
 *                      NCEditor expor edição dos contextos pós-criação.
 * @param {function} onSave - Callback para salvar edições
 * @param {function} onUnsavedChangesChange - TD-followup-prompt-unsaved-changes-spa
 *                      (Sessão 21): callback que NCEditor invoca quando
 *                      hasUnsavedChanges muda. App.jsx usa pra disparar
 *                      window.confirm em navegação SPA com edição dirty.
 */
function NCResultAnalysisSection({ analise, analiseV4, currentStatus, ncId, nc, onSave, onUnsavedChangesChange }) {
  // #219 Fase 2 — em RASCUNHO o editor (lápis-por-seção, sempre visível) é a
  // superfície ÚNICA da análise; o bloco read-only abaixo só renderiza em
  // status travado (aprovada/encerrada/em revisão), onde não há editor.
  // (A Fase 1 escondia o read-only só com o editor aberto; com o botão mestre
  // aposentado, o editor mostra leitura+edição no lugar — o estado
  // editorAberto/onEditingChange ficou obsoleto e foi removido.)
  const emRascunho = currentStatus === 'rascunho';

  const getSeverityClass = (severity) => {
    // TD86: usar cssSlug pra remover acentos. `.toLowerCase()` puro mantém
    // acento ("Média" → "média"), e o CSS espera ascii (`.severity-badge.media`).
    // Sem cssSlug, NCs "Média" e "Crítica" caíam no default sem cor.
    const s = cssSlug(severity) || 'media';
    return `severity-badge ${s}`;
  };

  // TD #48 (Sessão 24): botão "Recalcular com IA" foi removido daqui e
  // virou banner contextual smart em NCResult.jsx que aparece SÓ quando
  // contexto foi editado após última análise IA. Modal CascataIAModal
  // continua existindo (reusado pelo banner) — só mudou o gatilho.

  return (
    <section className="result-section">
      <div className="analysis-section-header">
        <h3><span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>search</span>Análise da NC</h3>
      </div>

      {/* Editor inline (lápis-por-seção) — superfície única em rascunho */}
      {emRascunho && (
        <NCEditor
          ncId={ncId}
          analise={analise}
          analiseV4={analiseV4}
          nc={nc}
          onSave={onSave}
          onUnsavedChangesChange={onUnsavedChangesChange}
        />
      )}

      <div className="analysis-header">
        {(() => {
          // #67: rótulo + cor custom do tenant. Cor custom vira FUNDO do badge
          // (com texto de contraste auto); null = mantém o token de tema (CSS).
          const corCustom = getSeveridadeCor(analise?.severidade);
          const estilo = corCustom
            ? { background: corCustom, color: corTextoContraste(corCustom) }
            : undefined;
          return (
            <span className={getSeverityClass(analise?.severidade)} style={estilo}>
              {getSeveridadeLabel(analise?.severidade)}
            </span>
          );
        })()}
        <span className="classification-badge">
          {analise?.classificacao || 'Não classificado'}
        </span>
      </div>

      {/* #219 Fase 2 — bloco read-only da análise: SÓ em status travado (sem
          editor). Em rascunho o editor acima mostra leitura e edição no lugar. */}
      {!emRascunho && (<>
      {/* #71/#73 (épico fluxo 8D) — Ação Imediata/Contenção SOBE pro TOPO da
          análise: no método (D3 do 8D), conter vem ANTES de investigar a
          causa. Era o último bloco da tela — a primeira coisa que um gestor
          faz estava enterrada. Só reordenação: zero mudança de dado. */}
      {analiseV4?.acao_contencao_imediata?.descricao && (
        <div className="analysis-item v4-contencao-section">
          <h4><span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px' }}>e911_emergency</span>Ação Imediata / Contenção</h4>
          <p className="v4-contencao-hint">O que foi feito AGORA para conter o problema — antes da investigação da causa (D3 do 8D).</p>
          <div className="v4-contencao-card">
            <p>{analiseV4.acao_contencao_imediata.descricao}</p>
            <div className="v4-contencao-meta">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><span className="material-symbols-outlined" style={{ fontSize: '14px' }}>schedule</span>Prazo: {analiseV4.acao_contencao_imediata.prazo_horas}h</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><span className="material-symbols-outlined" style={{ fontSize: '14px' }}>person</span>{analiseV4.acao_contencao_imediata.responsavel_funcao}</span>
            </div>
            {analiseV4.acao_contencao_imediata.criterio_conclusao && (
              <p className="v4-contencao-criterio">
                <strong>Critério de conclusão:</strong> {analiseV4.acao_contencao_imediata.criterio_conclusao}
              </p>
            )}
            {/* #70 (fluxo 8D) — frentes de contenção estruturada: tabela por
                local com quantidades e % NOK (computado no read). */}
            {Array.isArray(analiseV4.acao_contencao_imediata.frentes)
              && analiseV4.acao_contencao_imediata.frentes.length > 0 && (() => {
              const LOCAL_LABEL = {
                estoque: 'Estoque', em_transito: 'Em trânsito', no_cliente: 'No cliente',
                linha_producao: 'Linha de produção', outro: 'Outro',
              };
              const pct = (t, n) => {
                const T = Number(t); const N = Number(n);
                return Number.isFinite(T) && Number.isFinite(N) && T > 0 ? `${((N / T) * 100).toFixed(1)}%` : '—';
              };
              const th = { textAlign: 'left', fontSize: '12px', color: '#64748b', padding: '4px 6px' };
              const td = { padding: '3px 6px' };
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={th}>Frente</th>
                      <th style={th}>Descrição</th>
                      <th style={{ ...th, textAlign: 'right' }}>Qtd. inspecionada</th>
                      <th style={{ ...th, textAlign: 'right' }}>Qtd. NOK</th>
                      <th style={{ ...th, textAlign: 'right' }}>% NOK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analiseV4.acao_contencao_imediata.frentes.map((f, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={td}>{LOCAL_LABEL[f.local] || f.local}</td>
                        <td style={td}>{f.descricao || '—'}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{f.qtd_total ?? '—'}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{f.qtd_nok ?? '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{pct(f.qtd_total, f.qtd_nok)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      )}
      {/* Ação Imediata (legacy / fallback pré-v4) — mutual exclusion com o
          bloco v4 acima (Sessão 14 Briefing A-bis); reposicionado junto (#71). */}
      {analise?.acao_imediata
        && !analiseV4?.acao_contencao_imediata?.descricao && (
        <div className="analysis-item">
          <h4>Ação Imediata / Contenção</h4>
          <div className="immediate-action">
            {analise.acao_imediata}
          </div>
        </div>
      )}

      {/* #69 (fluxo 8D) — Abrangência multi-dimensional (D2): onde o problema
          FOI e NÃO FOI encontrado. Estende o MVP #280. */}
      {Array.isArray(analiseV4?.abrangencia) && analiseV4.abrangencia.length > 0 && (() => {
        const DIM_LABEL = {
          outros_lotes: 'Outros lotes', outros_produtos: 'Outros produtos',
          processos_similares: 'Processos similares', estoque: 'Estoque',
          em_transito: 'Em trânsito', no_cliente: 'No cliente', outro: 'Outro',
        };
        return (
          <div className="analysis-item">
            <h4><span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px' }}>zoom_out_map</span>Abrangência do problema</h4>
            <p className="v4-contencao-hint">Onde o problema FOI e NÃO FOI encontrado, por dimensão (D2 do 8D).</p>
            {analiseV4.abrangencia.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '4px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', fontSize: '14px' }}>
                <span style={{ fontWeight: 600, color: a.verificado ? '#166534' : '#92400e' }}>
                  {a.verificado ? '✔' : '○'}
                </span>
                <span style={{ minWidth: '160px', fontWeight: 500 }}>{DIM_LABEL[a.dimensao] || a.dimensao}</span>
                <span style={{ color: '#64748b' }}>{a.resultado || (a.verificado ? 'Verificado' : 'A verificar')}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* #242 (épico automotivo) — TRIAGEM 6M (verde/vermelho): priorização
          manual dos pilares como suspeita × a-testar (orienta a investigação;
          a IA não gera). Fica acima do 6M pra colorir a leitura dos pilares. */}
      {Array.isArray(analiseV4?.triagem_6m) && analiseV4.triagem_6m.length > 0 && (() => {
        const PILAR_LABEL = {
          mao_de_obra: 'Mão de obra', metodo: 'Método', maquina: 'Máquina',
          material: 'Material', medicao: 'Medição', meio_ambiente: 'Meio ambiente',
        };
        const cor = (s) => (s === 'suspeita' ? '#b91c1c' : s === 'a_testar' ? '#b45309' : '#64748b');
        const marca = (s) => (s === 'suspeita' ? '🔴' : '🟡');
        const rot = (s) => (s === 'suspeita' ? 'Suspeita' : 'A testar');
        return (
          <div className="analysis-item">
            <h4><span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px' }}>rule</span>Triagem 6M (priorização)</h4>
            <p className="v4-contencao-hint">Pilares priorizados para investigar: suspeita (perseguir) × a testar (ainda não examinado).</p>
            {analiseV4.triagem_6m.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '4px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', fontSize: '14px' }}>
                <span>{marca(t.status)}</span>
                <span style={{ minWidth: '160px', fontWeight: 500 }}>{PILAR_LABEL[t.pilar] || t.pilar}</span>
                <span style={{ color: cor(t.status), fontWeight: 500 }}>{rot(t.status)}</span>
                {t.nota && <span style={{ color: '#64748b' }}>· {t.nota}</span>}
              </div>
            ))}
          </div>
        );
      })()}

      {/* v4.0: Análise 6M (Ishikawa) */}
      {analiseV4?.compreensao?.analise_6m && (() => {
        // TD-followup-frontend-mostrar-aviso-pilar (Sessão 21): complemento
        // visual do soft warning backend. Mostra ⚠️ ao lado do badge "Pilar
        // principal" quando o pilar escolhido aponta pra ramo do 6M sem
        // conteúdo substantivo. Heurística espelhada em pilarCoerencia.js
        // (paridade com analysis_field_validator.py:diagnosticar_pilar_principal_vs_conteudo).
        const _diag = diagnosticarPilarPrincipal(
          analiseV4.compreensao.analise_6m.pilar_principal,
          analiseV4.compreensao.analise_6m
        );
        return (
          <div className="analysis-item v4-6m-section">
            <h4><span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px', textTransform: 'none' }}>account_tree</span>Análise 6M (Ishikawa)</h4>
            <div className="v4-6m-grid">
              {[
                ['Mão de obra', analiseV4.compreensao.analise_6m.mao_de_obra, 'engineering'],
                ['Método', analiseV4.compreensao.analise_6m.metodo, 'checklist'],
                ['Máquina', analiseV4.compreensao.analise_6m.maquina, 'settings'],
                ['Material', analiseV4.compreensao.analise_6m.material, 'category'],
                ['Medição', analiseV4.compreensao.analise_6m.medicao, 'straighten'],
                ['Meio ambiente', analiseV4.compreensao.analise_6m.meio_ambiente, 'thermostat'],
              ].filter(([, value]) => ramo6MTemConteudo(value)).map(([label, value, icon]) => (
                <div
                  key={label}
                  className={`v4-6m-item ${label === analiseV4.compreensao.analise_6m.pilar_principal ? 'v4-6m-principal' : ''}`}
                >
                  <div className="v4-6m-label"><span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '4px', textTransform: 'none' }}>{icon}</span>{label}</div>
                  <div className="v4-6m-value">{value}</div>
                  {label === analiseV4.compreensao.analise_6m.pilar_principal && (
                    <span className="v4-6m-badge">
                      Pilar principal
                      {!_diag.coerente && (
                        <span
                          className="material-symbols-outlined v4-6m-pilar-incoerente"
                          style={{ fontSize: '14px', verticalAlign: 'middle', marginLeft: '4px' }}
                          title={_diag.motivo || 'Pilar aponta para um ramo do 6M sem conteúdo substantivo.'}
                          role="img"
                          aria-label="Aviso de incoerência do pilar principal"
                        >
                          warning
                        </span>
                      )}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="analysis-item">
        <h4>Causa Raiz <OrigemBadge campo="causa_raiz" analiseV4={analiseV4} /></h4>
        <p>{analise?.causa_raiz || 'Não identificada'}</p>
        
        {/* v4.0: Confiança e justificativa */}
        {analiseV4?.causa_raiz?.confianca && (
          <div className="v4-causa-meta">
            <span className={`v4-confianca v4-confianca-${(analiseV4.causa_raiz.confianca || '').toLowerCase()}`}>
              Confiança: {analiseV4.causa_raiz.confianca}
            </span>
            {analiseV4.causa_raiz.justificativa_escolha && (
              <p className="v4-justificativa">{analiseV4.causa_raiz.justificativa_escolha}</p>
            )}
          </div>
        )}
      </div>

      {analise?.cinco_porques && analise.cinco_porques.length > 0 && (
        <div className="analysis-item">
          {/* #241 — quando a cadeia de DETECÇÃO existe, a canônica ganha o
              rótulo de vertente "ocorrência" (rótulo, nunca campo novo —
              decisão 08/07). Sem detecção → título de sempre, byte-idêntico. */}
          <h4>
            Análise dos Porquês
            {Array.isArray(analiseV4?.porques_deteccao) && analiseV4.porques_deteccao.length > 0 && (
              <small style={{ fontWeight: 400, color: '#64748b' }}> — por que ocorreu?</small>
            )}
          </h4>
          <ol className="porques-list">
            {/*
              HOTFIX Sessão 20: cinco_porques agora pode vir em 2 shapes:
                - Legacy: ["Por que X? Porque Y.", ...] (string formatada)
                - v4: [{numero, pergunta, resposta}, ...] (objeto)
              Após PR feat/shape-migration-cinco-porques (Sessão 20),
              NCs antigas são migradas on-write pra v4. Sem este normalize
              o filter `p.trim()` quebrava com `TypeError: e.trim is not
              a function` quando p era dict — quebrava a tela inteira
              via React error boundary (NCEditor/NCResult).
              `formatHipoteseAlternativaPorque` aceita dict ou string
              (retorna o input se já é string).
            */}
            {analise.cinco_porques
              .map(p => typeof p === 'string' ? p : formatHipoteseAlternativaPorque(p))
              .filter(s => s && typeof s === 'string' && s.trim())
              .map((texto, idx) => (
                <li key={idx}>{texto}</li>
              ))}
          </ol>
        </div>
      )}

      {/* #241 (épico automotivo) — cadeia de DETECÇÃO (D4 do 8D): "por que
          NÃO foi detectado?". Overlay manual (a IA não gera); só renderiza
          quando preenchida. Lida do analiseV4 (path único porques_deteccao). */}
      {Array.isArray(analiseV4?.porques_deteccao) && analiseV4.porques_deteccao.length > 0 && (
        <div className="analysis-item">
          <h4><span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px' }}>visibility_off</span>Análise dos Porquês — por que não foi detectado?</h4>
          <p className="v4-contencao-hint">Cadeia de detecção (D4 do 8D): por que o problema escapou dos controles até chegar ao cliente.</p>
          <ol className="porques-list">
            {analiseV4.porques_deteccao.map((p, idx) => (
              <li key={idx}>{p?.pergunta ? `${p.pergunta} ${p.resposta || ''}` : (p?.resposta || '')}</li>
            ))}
          </ol>
        </div>
      )}

      {/* v4.0: Hipótese Alternativa (atual) */}
      {analiseV4?.hipotese_alternativa?.declaracao && (
        <div className="analysis-item v4-hipotese-section">
          <h4><span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px' }}>lightbulb</span>Hipótese Alternativa</h4>
          <div className="v4-hipotese-card">
            <p className="v4-hipotese-declaracao">{analiseV4.hipotese_alternativa.declaracao}</p>
            {analiseV4.hipotese_alternativa.porques?.length > 0 && (
              <div className="v4-hipotese-porques">
                <strong>Cadeia de Porquês:</strong>
                <ol>
                  {analiseV4.hipotese_alternativa.porques.map((p, idx) => (
                    <li key={idx}>
                      {formatHipoteseAlternativaPorque(p)}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <p className="v4-hipotese-razao">
              <strong>Por que não selecionada:</strong> {analiseV4.hipotese_alternativa.por_que_nao_selecionada}
            </p>
          </div>
        </div>
      )}

      {/* J1: Hipótese alternativa ORIGINAL (preservada antes da reanálise) — só aparece
          quando NÃO há hipotese_alternativa atual e existe a snapshot pré-reanálise. */}
      {!analiseV4?.hipotese_alternativa?.declaracao && analiseV4?.hipotese_alternativa_original?.declaracao && (
        <div className="analysis-item v4-hipotese-section">
          <h4 style={{ color: '#64748b' }}><span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px', textTransform: 'none' }}>history_edu</span>Hipótese Alternativa <small>(original — antes da reanálise)</small></h4>
          <div className="v4-hipotese-card" style={{ background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
            <p className="v4-hipotese-declaracao">{analiseV4.hipotese_alternativa_original.declaracao}</p>
            {analiseV4.hipotese_alternativa_original.porques?.length > 0 && (
              <div className="v4-hipotese-porques">
                <strong>Cadeia de Porquês (original):</strong>
                <ol>
                  {analiseV4.hipotese_alternativa_original.porques.map((p, idx) => (
                    <li key={idx}>
                      {formatHipoteseAlternativaPorque(p)}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {analiseV4?.causa_raiz_original?.declaracao && (
              <p style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>
                <strong>Causa raiz inicial:</strong>{' '}
                {typeof analiseV4.causa_raiz_original === 'string'
                  ? analiseV4.causa_raiz_original
                  : analiseV4.causa_raiz_original.declaracao}
              </p>
            )}
          </div>
        </div>
      )}

      {/* #71 — os blocos de Ação Imediata/Contenção (v4 + legacy fallback,
          Sessão 14 Briefing A-bis) foram MOVIDOS pro topo da análise (antes
          do 6M): no 8D, conter (D3) vem antes de investigar (D4). */}

      {analise?.acoes_corretivas && analise.acoes_corretivas.length > 0 && (
        <div className="analysis-item">
          <h4>Ações Corretivas</h4>
          <div className="actions-list">
            {analise.acoes_corretivas.map((acao, idx) => {
              const a = normalizarAcaoCorretiva(acao);
              // #241 — `vertente` vive SÓ no shape V4 (o sync legado dropa
              // campos novos — lição #66/concluida_em): lê do analiseV4 pelo
              // MESMO índice (arrays alinhados 1:1); fallback pro item local.
              const vertente = analiseV4?.acoes_corretivas?.[idx]?.vertente || acao.vertente || '';
              const VERTENTE_LABEL = { ocorrencia: 'Ocorrência', deteccao: 'Detecção', sistemica: 'Sistêmica' };
              return (
                <div key={idx} className="action-card">
                  <div className="action-text">{a.descricao || '—'}</div>
                  {a.responsavel && (
                    <div className="action-responsible" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span className="material-symbols-outlined" style={{ fontSize: '14px' }}>person</span>{a.responsavel}</div>
                  )}
                  {a.prazo && (
                    <div className="action-deadline" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span className="material-symbols-outlined" style={{ fontSize: '14px' }}>schedule</span>{a.prazo}</div>
                  )}
                  {a.prioridade && (
                    <div className="action-priority" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span className="material-symbols-outlined" style={{ fontSize: '14px' }}>flag</span>{a.prioridade}</div>
                  )}
                  {VERTENTE_LABEL[vertente] && (
                    <div className="action-vertente" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b' }} title="Vertente 8D: a qual cadeia esta ação responde (ocorrência × detecção × sistêmica)"><span className="material-symbols-outlined" style={{ fontSize: '14px' }}>alt_route</span>{VERTENTE_LABEL[vertente]}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {analise?.padrao_identificado && (
        <div className="pattern-alert" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>warning</span>Padrão identificado: {analise.padrao_identificado}
        </div>
      )}
      </>)}
    </section>
  );
}

export default NCResultAnalysisSection;